"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useState } from "react";
import dynamic from "next/dynamic";
import type { SearchEntityResult } from "fansunited-data-layer";
import type { FUSportsEventDetails } from "../data/types";
import { PageNavigation } from "@/components/layout/PageNavigation";
import type { ResolvedHeaderColors } from "@/features/theme/resolveBrandedStyle";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { SportEventHeader } from "./SportEventHeader";
import { OverviewTab } from "../tabs/OverviewTab";
import type { SportEventSport } from "../utils/sportShape";
import { getPeriodLabelKey } from "../utils/sportShape";
const StatsTab = dynamic(() => import("../tabs/StatsTab").then(m => ({
  default: m.StatsTab
})));
const OddsTab = dynamic(() => import("../tabs/OddsTab").then(m => ({
  default: m.OddsTab
})));
const FormRow = dynamic(() => import("./FormRow").then(m => ({
  default: m.FormRow
})));
const H2HPanel = dynamic(() => import("./H2HPanel").then(m => ({
  default: m.H2HPanel
})));
const EventStandings = dynamic(() => import("./EventStandings").then(m => ({
  default: m.EventStandings
})));
const EventLineups = dynamic(() => import("./EventLineups").then(m => ({
  default: m.EventLineups
})));
type TabId = "overview" | "odds" | "stats" | "form" | "h2h" | "standings" | "lineups";
interface Props {
  details: FUSportsEventDetails;
  entities: Record<string, SearchEntityResult>;
  sport: SportEventSport;
  headerColors?: ResolvedHeaderColors;
  breadcrumbs?: React.ReactNode;
}
export function SportEventContent(t0) {
  const $ = _c(104);
  const {
    details,
    entities,
    sport,
    headerColors,
    breadcrumbs
  } = t0;
  const labels = useLabels();
  const {
    hasOdds
  } = useTenantSettings();
  const t = labels.sportEventPage;
  const periodLabelKey = getPeriodLabelKey(sport);
  let t1;
  if ($[0] !== t.scoreBreakdown.quarter || $[1] !== t.scoreBreakdown.quarterShort) {
    t1 = {
      prefix: t.scoreBreakdown.quarterShort,
      header: t.scoreBreakdown.quarter
    };
    $[0] = t.scoreBreakdown.quarter;
    $[1] = t.scoreBreakdown.quarterShort;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t.scoreBreakdown.period || $[4] !== t.scoreBreakdown.periodShort) {
    t2 = {
      prefix: t.scoreBreakdown.periodShort,
      header: t.scoreBreakdown.period
    };
    $[3] = t.scoreBreakdown.period;
    $[4] = t.scoreBreakdown.periodShort;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t.scoreBreakdown.set || $[7] !== t.scoreBreakdown.setShort) {
    t3 = {
      prefix: t.scoreBreakdown.setShort,
      header: t.scoreBreakdown.set
    };
    $[6] = t.scoreBreakdown.set;
    $[7] = t.scoreBreakdown.setShort;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      quarter: t1,
      period: t2,
      set: t3
    };
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const periodMeta = t4[periodLabelKey];
  const isFinished = details.data.event.status.type === "FINISHED";
  let list;
  if ($[13] !== details.data.event.competitorOne.stats.length || $[14] !== details.data.event.competitorTwo || $[15] !== details.data.event.lineups || $[16] !== details.data.event.mainEvents.length || $[17] !== details.data.event.score.length || $[18] !== details.data.form.competitorOne.latestOverall.length || $[19] !== details.data.form.competitorTwo || $[20] !== details.data.headToHead.events || $[21] !== details.data.headToHead.summary.length || $[22] !== details.data.standings?.groups || $[23] !== details.data.stats.competitorOne || $[24] !== details.data.stats.competitorTwo || $[25] !== hasOdds || $[26] !== isFinished || $[27] !== labels.matchPage.tabs.odds || $[28] !== t.tabs) {
    list = [];
    const hasScoreLines = details.data.event.score.length > 0;
    const hasMainEvents = details.data.event.mainEvents.length > 0;
    if (hasScoreLines || hasMainEvents) {
      let t5;
      if ($[30] !== t.tabs.overview) {
        t5 = {
          id: "overview",
          label: t.tabs.overview
        };
        $[30] = t.tabs.overview;
        $[31] = t5;
      } else {
        t5 = $[31];
      }
      list.push(t5);
    }
    if (hasOdds && !isFinished) {
      let t5;
      if ($[32] !== labels.matchPage.tabs.odds) {
        t5 = {
          id: "odds",
          label: labels.matchPage.tabs.odds
        };
        $[32] = labels.matchPage.tabs.odds;
        $[33] = t5;
      } else {
        t5 = $[33];
      }
      list.push(t5);
    }
    const hasMatchStats = details.data.event.competitorOne.stats.length > 0 || details.data.event.competitorTwo.stats.length > 0;
    const hasAggregate = (details.data.stats.competitorOne.length > 0 || details.data.stats.competitorTwo.length > 0) && [...details.data.stats.competitorOne, ...details.data.stats.competitorTwo].some(_temp);
    if (hasMatchStats || hasAggregate) {
      let t5;
      if ($[34] !== t.tabs.stats) {
        t5 = {
          id: "stats",
          label: t.tabs.stats
        };
        $[34] = t.tabs.stats;
        $[35] = t5;
      } else {
        t5 = $[35];
      }
      list.push(t5);
    }
    const hasForm = details.data.form.competitorOne.latestOverall.length > 0 || details.data.form.competitorTwo.latestOverall.length > 0;
    if (hasForm) {
      let t5;
      if ($[36] !== t.tabs.form) {
        t5 = {
          id: "form",
          label: t.tabs.form
        };
        $[36] = t.tabs.form;
        $[37] = t5;
      } else {
        t5 = $[37];
      }
      list.push(t5);
    }
    const hasH2H = details.data.headToHead.summary.length > 0 || details.data.headToHead.events.length > 0;
    if (hasH2H) {
      let t5;
      if ($[38] !== t.tabs.h2h) {
        t5 = {
          id: "h2h",
          label: t.tabs.h2h
        };
        $[38] = t.tabs.h2h;
        $[39] = t5;
      } else {
        t5 = $[39];
      }
      list.push(t5);
    }
    if (details.data.standings?.groups.some(_temp2)) {
      let t5;
      if ($[40] !== t.tabs.standings) {
        t5 = {
          id: "standings",
          label: t.tabs.standings
        };
        $[40] = t.tabs.standings;
        $[41] = t5;
      } else {
        t5 = $[41];
      }
      list.push(t5);
    }
    const lineups = details.data.event.lineups;
    const hasLineups = !!lineups && (lineups.competitorOne.starting.length > 0 || lineups.competitorTwo.starting.length > 0 || lineups.competitorOne.substitutes.length > 0 || lineups.competitorTwo.substitutes.length > 0);
    if (hasLineups) {
      let t5;
      if ($[42] !== t.tabs.lineups) {
        t5 = {
          id: "lineups",
          label: t.tabs.lineups
        };
        $[42] = t.tabs.lineups;
        $[43] = t5;
      } else {
        t5 = $[43];
      }
      list.push(t5);
    }
    $[13] = details.data.event.competitorOne.stats.length;
    $[14] = details.data.event.competitorTwo;
    $[15] = details.data.event.lineups;
    $[16] = details.data.event.mainEvents.length;
    $[17] = details.data.event.score.length;
    $[18] = details.data.form.competitorOne.latestOverall.length;
    $[19] = details.data.form.competitorTwo;
    $[20] = details.data.headToHead.events;
    $[21] = details.data.headToHead.summary.length;
    $[22] = details.data.standings?.groups;
    $[23] = details.data.stats.competitorOne;
    $[24] = details.data.stats.competitorTwo;
    $[25] = hasOdds;
    $[26] = isFinished;
    $[27] = labels.matchPage.tabs.odds;
    $[28] = t.tabs;
    $[29] = list;
  } else {
    list = $[29];
  }
  const tabs = list;
  const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? "overview");
  let t5;
  if ($[44] !== breadcrumbs || $[45] !== details || $[46] !== entities || $[47] !== headerColors || $[48] !== sport) {
    t5 = <SportEventHeader details={details} entities={entities} sport={sport} headerColors={headerColors} breadcrumbs={breadcrumbs} />;
    $[44] = breadcrumbs;
    $[45] = details;
    $[46] = entities;
    $[47] = headerColors;
    $[48] = sport;
    $[49] = t5;
  } else {
    t5 = $[49];
  }
  let t6;
  if ($[50] !== activeTab || $[51] !== tabs) {
    t6 = <PageNavigation options={tabs} activeId={activeTab} onChange={setActiveTab} sticky={true} />;
    $[50] = activeTab;
    $[51] = tabs;
    $[52] = t6;
  } else {
    t6 = $[52];
  }
  let t7;
  if ($[53] !== activeTab || $[54] !== details || $[55] !== entities || $[56] !== periodMeta || $[57] !== sport) {
    t7 = activeTab === "overview" && <OverviewTab details={details} entities={entities} sport={sport} periodLabelPrefix={periodMeta.prefix} periodHeaderLabel={periodMeta.header} />;
    $[53] = activeTab;
    $[54] = details;
    $[55] = entities;
    $[56] = periodMeta;
    $[57] = sport;
    $[58] = t7;
  } else {
    t7 = $[58];
  }
  let t8;
  if ($[59] !== activeTab || $[60] !== details || $[61] !== entities || $[62] !== sport) {
    t8 = activeTab === "odds" && <OddsTab details={details} entities={entities} sport={sport} />;
    $[59] = activeTab;
    $[60] = details;
    $[61] = entities;
    $[62] = sport;
    $[63] = t8;
  } else {
    t8 = $[63];
  }
  let t9;
  if ($[64] !== activeTab || $[65] !== details || $[66] !== entities || $[67] !== sport) {
    t9 = activeTab === "stats" && <StatsTab details={details} entities={entities} sport={sport} />;
    $[64] = activeTab;
    $[65] = details;
    $[66] = entities;
    $[67] = sport;
    $[68] = t9;
  } else {
    t9 = $[68];
  }
  let t10;
  if ($[69] !== activeTab || $[70] !== details || $[71] !== entities || $[72] !== sport || $[73] !== t.form) {
    t10 = activeTab === "form" && <FormRow details={details} entities={entities} sport={sport} heading={t.form.title} />;
    $[69] = activeTab;
    $[70] = details;
    $[71] = entities;
    $[72] = sport;
    $[73] = t.form;
    $[74] = t10;
  } else {
    t10 = $[74];
  }
  let t11;
  if ($[75] !== activeTab || $[76] !== details || $[77] !== entities || $[78] !== sport || $[79] !== t.h2h) {
    t11 = activeTab === "h2h" && <H2HPanel details={details} entities={entities} sport={sport} heading={t.h2h.title} recentHeading={t.h2h.recentMeetings} />;
    $[75] = activeTab;
    $[76] = details;
    $[77] = entities;
    $[78] = sport;
    $[79] = t.h2h;
    $[80] = t11;
  } else {
    t11 = $[80];
  }
  let t12;
  if ($[81] !== activeTab || $[82] !== details || $[83] !== entities || $[84] !== sport || $[85] !== t.standings) {
    t12 = activeTab === "standings" && <EventStandings details={details} entities={entities} sport={sport} heading={t.standings.title} />;
    $[81] = activeTab;
    $[82] = details;
    $[83] = entities;
    $[84] = sport;
    $[85] = t.standings;
    $[86] = t12;
  } else {
    t12 = $[86];
  }
  let t13;
  if ($[87] !== activeTab || $[88] !== details || $[89] !== entities || $[90] !== t.lineups) {
    t13 = activeTab === "lineups" && <EventLineups details={details} entities={entities} heading={t.lineups.title} />;
    $[87] = activeTab;
    $[88] = details;
    $[89] = entities;
    $[90] = t.lineups;
    $[91] = t13;
  } else {
    t13 = $[91];
  }
  let t14;
  if ($[92] !== t10 || $[93] !== t11 || $[94] !== t12 || $[95] !== t13 || $[96] !== t7 || $[97] !== t8 || $[98] !== t9) {
    t14 = <div className="max-w-350 mx-auto px-4 py-6 md:py-8">{t7}{t8}{t9}{t10}{t11}{t12}{t13}</div>;
    $[92] = t10;
    $[93] = t11;
    $[94] = t12;
    $[95] = t13;
    $[96] = t7;
    $[97] = t8;
    $[98] = t9;
    $[99] = t14;
  } else {
    t14 = $[99];
  }
  let t15;
  if ($[100] !== t14 || $[101] !== t5 || $[102] !== t6) {
    t15 = <div className="min-h-screen">{t5}{t6}{t14}</div>;
    $[100] = t14;
    $[101] = t5;
    $[102] = t6;
    $[103] = t15;
  } else {
    t15 = $[103];
  }
  return t15;
}
function _temp2(g) {
  return g.standings.length > 0;
}
function _temp(s) {
  return s.value.total !== "0";
}